import type { CSSProperties, ReactNode, ButtonHTMLAttributes } from "react";

/* ── Stars ──────────────────────────────────────────────── */
export function StarRating({ rating, size = 13 }: { rating: number; size?: number }) {
  return (
    <span style={{ fontSize: size, display: "inline-flex", gap: 1, lineHeight: 1 }}>
      {[1, 2, 3, 4, 5].map((i) => {
        const full = i <= Math.floor(rating);
        const half = !full && i === Math.ceil(rating) && rating % 1 >= 0.5;
        return (
          <span key={i} style={{ color: full || half ? "var(--color-star)" : "rgba(255,255,255,0.15)", opacity: half ? 0.65 : 1 }}>★</span>
        );
      })}
    </span>
  );
}

/* ── Price display ──────────────────────────────────────── */
export function PriceDisplay({ price, originalPrice, size = "md" }: { price: number; originalPrice: number; size?: "sm" | "md" | "lg" }) {
  const fs = { sm: 13, md: 16, lg: 22 }[size];
  if (price === 0)
    return <span style={{ color: "var(--color-free)", fontWeight: 800, fontSize: fs }}>FREE</span>;
  const onSale = price < originalPrice && originalPrice > 0;
  return (
    <span style={{ display: "inline-flex", alignItems: "baseline", gap: 8 }}>
      {onSale && (
        <span style={{ color: "var(--text-muted)", textDecoration: "line-through", fontSize: fs - 3 }}>
          ${originalPrice.toFixed(2)}
        </span>
      )}
      <span style={{ color: onSale ? "var(--color-sale)" : "var(--text-primary)", fontWeight: 800, fontSize: fs }}>
        ${price.toFixed(2)}
      </span>
    </span>
  );
}

/* ── Sale percent badge ─────────────────────────────────── */
export function SalePct({ price, originalPrice }: { price: number; originalPrice: number }) {
  if (price >= originalPrice || originalPrice === 0) return null;
  const pct = Math.round((1 - price / originalPrice) * 100);
  return <span className="badge badge-sale">-{pct}%</span>;
}

/* ── Button ─────────────────────────────────────────────── */
type BtnVariant = "primary" | "ghost" | "outline" | "danger" | "psplus";
interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: BtnVariant;
  size?: "sm" | "md" | "lg";
  fullWidth?: boolean;
  icon?: ReactNode;
}
export function Btn({ variant = "primary", size = "md", fullWidth, icon, children, style, ...rest }: BtnProps) {
  const pad = { sm: "7px 16px", md: "10px 22px", lg: "13px 30px" }[size];
  const fs  = { sm: 13, md: 14, lg: 15 }[size];
  return (
    <button
      className={`btn btn-${variant}`}
      style={{ padding: pad, fontSize: fs, width: fullWidth ? "100%" : undefined, ...style }}
      {...rest}
    >
      {icon && <span style={{ fontSize: fs + 2 }}>{icon}</span>}
      {children}
    </button>
  );
}

/* ── Skeleton loader ────────────────────────────────────── */
export function Skel({ w, h, radius, style }: { w?: string | number; h?: string | number; radius?: string; style?: CSSProperties }) {
  return (
    <div
      className="skel"
      style={{ width: w, height: h ?? 16, borderRadius: radius ?? "var(--radius-sm)", ...style }}
    />
  );
}

/* ── Section header row ─────────────────────────────────── */
export function SectionHeader({
  title, label, action, onAction,
}: { title: string; label?: string; action?: string; onAction?: () => void }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 18 }}>
      <div>
        {label && <div className="section-label" style={{ marginBottom: 4 }}>{label}</div>}
        <h2 className="section-heading">{title}</h2>
      </div>
      {action && (
        <button
          onClick={onAction}
          style={{
            background: "none", border: "none", color: "var(--text-accent)",
            fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-body)",
            display: "flex", alignItems: "center", gap: 4,
          }}
        >
          {action} →
        </button>
      )}
    </div>
  );
}

/* ── Empty state ────────────────────────────────────────── */
export function EmptyState({ icon, title, sub }: { icon: string; title: string; sub?: string }) {
  return (
    <div style={{ textAlign: "center", padding: "80px 24px", color: "var(--text-muted)" }}>
      <div style={{ fontSize: 52, marginBottom: 16, opacity: 0.5 }}>{icon}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: "var(--text-secondary)", marginBottom: 8 }}>{title}</div>
      {sub && <div style={{ fontSize: 14 }}>{sub}</div>}
    </div>
  );
}

/* ── Divider ────────────────────────────────────────────── */
export function Divider({ style }: { style?: CSSProperties }) {
  return <div className="divider" style={style} />;
}

/* ── Avatar ─────────────────────────────────────────────── */
export function Avatar({ name, size = 36 }: { name: string; size?: number }) {
  const colors = ["#0072CE", "#7b5af5", "#21c179", "#ff6b35", "#c6a84b"];
  const color = colors[name.charCodeAt(0) % colors.length];
  return (
    <div
      style={{
        width: size, height: size, borderRadius: "50%", background: color,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "var(--font-display)", fontWeight: 900, fontSize: size * 0.42, color: "#fff",
        flexShrink: 0,
      }}
    >
      {name[0].toUpperCase()}
    </div>
  );
}

/* ── PS Plus price row ──────────────────────────────────── */
export function PSPlusBadge({ psplusPrice }: { psplusPrice: number }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
      <span className="badge badge-psplus">PS+</span>
      <span style={{ color: "var(--psplus)", fontWeight: 700, fontSize: 14 }}>${psplusPrice.toFixed(2)}</span>
    </div>
  );
}
